import { useState, useEffect, useCallback, useRef } from 'react';
import { GameOutput, DefaultService } from '../generated-api';
import { useData } from '../contexts/DataContext';

export interface RecentGame {
  game: GameOutput;
  lastPlayedAt: string;
}

interface UseRecentGamesReturn {
  recentGames: RecentGame[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;
const CACHE_KEY = 'recentGames';

interface CachedData {
  gameIds: string[];
  lastPlayedDates: Record<string, string>;
  timestamp: number;
}

const getCachedData = (): CachedData | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: CachedData = JSON.parse(cached);
    if (Date.now() - data.timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const setCachedData = (gameIds: string[], lastPlayedDates: Record<string, string>) => {
  const data: CachedData = {
    gameIds,
    lastPlayedDates,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
};

export const useRecentGames = (limit: number = 4): UseRecentGamesReturn => {
  const { games, fetchGames } = useData();
  const [recentGames, setRecentGames] = useState<RecentGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchPromise = useRef<Promise<void> | null>(null);

  const processRecentGames = useCallback((
    gameIds: string[],
    lastPlayedDates: Record<string, string>,
    availableGames: GameOutput[]
  ): RecentGame[] => {
    const result: RecentGame[] = [];

    for (const gameId of gameIds) {
      if (result.length >= limit) break;

      const game = availableGames.find(g => g.id === gameId);
      if (game) {
        result.push({
          game,
          lastPlayedAt: lastPlayedDates[gameId] || '',
        });
      }
    }

    return result;
  }, [limit]);

  const fetchRecentGames = useCallback(async (force = false) => {
    // Check cache first
    if (!force) {
      const cached = getCachedData();
      if (cached && games.length > 0) {
        const processed = processRecentGames(cached.gameIds, cached.lastPlayedDates, games);
        if (processed.length > 0) {
          setRecentGames(processed);
          setLoading(false);
          return;
        }
      }
    }

    // Deduplicate concurrent requests
    if (fetchPromise.current) {
      return fetchPromise.current;
    }

    setLoading(true);
    setError(null);

    const promise = (async () => {
      try {
        // Ensure games are loaded
        const availableGames = games.length > 0 ? games : await fetchGames();

        // Fetch recent plays
        const plays = await DefaultService.getPlaysPlaysGet({
          limit: 50, // Fetch more to ensure we get enough unique games
          sort: 'newest'
        });

        // Extract unique gameIds preserving recency order
        const gameIds: string[] = [];
        const lastPlayedDates: Record<string, string> = {};

        for (const play of plays) {
          if (!gameIds.includes(play.gameId)) {
            gameIds.push(play.gameId);
            lastPlayedDates[play.gameId] = play.playDate;
          }
        }

        // Cache the results
        setCachedData(gameIds, lastPlayedDates);

        // Process and set state
        const processed = processRecentGames(gameIds, lastPlayedDates, availableGames);
        setRecentGames(processed);
      } catch (err) {
        console.error('Error fetching recent games:', err);
        setError('Failed to load recent games');
      } finally {
        setLoading(false);
        fetchPromise.current = null;
      }
    })();

    fetchPromise.current = promise;
    return promise;
  }, [games, fetchGames, processRecentGames]);

  const refresh = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    fetchRecentGames(true);
  }, [fetchRecentGames]);

  useEffect(() => {
    fetchRecentGames();
  }, [fetchRecentGames]);

  return { recentGames, loading, error, refresh };
};

export default useRecentGames;
